import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from 'react-native';
import DLOButton, { PlusIcon, LocationIcon } from './DLOButton';

export interface DLOTitleAction {
  text: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
  iconLeading?: React.ReactNode;
  iconTrailing?: React.ReactNode;
  onPress?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export interface DLOTitleProps {
  title: string;
  subtitle?: string;
  actions?: DLOTitleAction[];
  primaryAction?: DLOTitleAction;
  secondaryAction?: DLOTitleAction;
  style?: any;
  titleStyle?: any;
  actionsContainerStyle?: any;
}

const DLOTitle: React.FC<DLOTitleProps> = ({
  title,
  subtitle,
  actions,
  primaryAction,
  secondaryAction,
  style,
  titleStyle,
  actionsContainerStyle,
}) => {
  // Build actions array from props
  const allActions = actions || [];
  
  // Add primary and secondary actions if provided
  if (secondaryAction) {
    allActions.push(secondaryAction);
  }
  if (primaryAction) {
    allActions.push(primaryAction);
  }

  return (
    <View style={[styles.container, style]}>
      {/* Title Section */}
      <View style={styles.titleSection}>
        <Text style={[styles.title, titleStyle]} numberOfLines={1}>
          {title}
        </Text>
        {subtitle && (
          <Text style={styles.subtitle} numberOfLines={2}>
            {subtitle}
          </Text>
        )}
      </View>

      {/* Actions Section */}
      {allActions.length > 0 && (
        <View style={[styles.actionsContainer, actionsContainerStyle]}>
          {allActions.map((action, index) => (
            <DLOButton
              key={index}
              text={action.text}
              variant={action.variant || 'secondary'}
              size="large"
              iconLeading={action.iconLeading}
              iconTrailing={action.iconTrailing}
              onPress={action.onPress}
              disabled={action.disabled}
              isLoading={action.isLoading}
            />
          ))}
        </View>
      )}
    </View>
  );
};

// Convenience component for the common Labor Optimization layout
export const DLOLaborOptimizationTitle: React.FC<{
  onCreateSchedule?: () => void;
  onLocationSettings?: () => void;
  isCreatingSchedule?: boolean;
  style?: any;
}> = ({
  onCreateSchedule,
  onLocationSettings,
  isCreatingSchedule = false,
  style,
}) => (
  <DLOTitle
    title="Labor Optimization"
    primaryAction={{
      text: "Create Schedule",
      variant: "primary",
      iconLeading: <PlusIcon />,
      onPress: onCreateSchedule,
      isLoading: isCreatingSchedule,
    }}
    secondaryAction={{
      text: "Location Settings",
      variant: "primary",
      iconLeading: <LocationIcon color="#FFFFFF" />,
      onPress: onLocationSettings,
    }}
    style={style}
  />
);

// Convenience component for dashboard-style titles
export const DLODashboardTitle: React.FC<{
  title: string;
  subtitle?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  primaryActionText?: string;
  secondaryActionText?: string;
  primaryActionIcon?: React.ReactNode;
  secondaryActionIcon?: React.ReactNode;
  style?: any;
}> = ({
  title,
  subtitle,
  onPrimaryAction,
  onSecondaryAction,
  primaryActionText = "Create",
  secondaryActionText = "Settings",
  primaryActionIcon = <PlusIcon />,
  secondaryActionIcon = <LocationIcon />,
  style,
}) => {
  const actions: DLOTitleAction[] = [];

  if (onSecondaryAction) {
    actions.push({
      text: secondaryActionText,
      variant: "secondary",
      iconLeading: secondaryActionIcon,
      onPress: onSecondaryAction,
    });
  }

  if (onPrimaryAction) {
    actions.push({
      text: primaryActionText,
      variant: "primary",
      iconLeading: primaryActionIcon,
      onPress: onPrimaryAction,
    });
  }

  return (
    <DLOTitle
      title={title}
      subtitle={subtitle}
      actions={actions}
      style={style}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    minHeight: 56,
    width: '100%',
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderColor: 'transparent',
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  titleSection: {
    flex: 1,
    gap: 4,
    paddingRight: 16,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderColor: 'transparent',
  },
  title: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 28,
    fontWeight: '700',
    lineHeight: 40,
  },
  subtitle: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    marginTop: 4,
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    flexShrink: 0,
  },
});

export default DLOTitle;
